import type { ToastVariant } from '@/components/ui/toast';

// Toast queue for when toaster is not yet mounted
let toastQueue: Array<{
  message: string;
  variant: ToastVariant;
  duration?: number;
}> = [];

// Global toast function reference
let globalAddToast:
  | ((toast: {
      message: string;
      variant: ToastVariant;
      duration?: number;
    }) => string)
  | null = null;

/**
 * Register the toast function from the ToasterProvider
 * This is called internally by the toaster component
 */
export const registerToast = (
  addToastFn: (toast: {
    message: string;
    variant: ToastVariant;
    duration?: number;
  }) => string
) => {
  globalAddToast = addToastFn;

  // Process queued toasts
  if (toastQueue.length > 0) {
    toastQueue.forEach((queuedToast) => {
      addToastFn(queuedToast);
    });
    toastQueue = [];
  }
};

/**
 * Unregister the toast function
 * This is called internally when the toaster unmounts
 */
export const unregisterToast = () => {
  globalAddToast = null;
};

/**
 * Base function to show a toast notification
 * If toaster is not mounted, toasts are queued
 */
const showToast = (
  message: string,
  variant: ToastVariant,
  duration?: number
): string | null => {
  const toastData = { message, variant, duration };

  if (globalAddToast) {
    return globalAddToast(toastData);
  } else {
    // Queue toast if toaster not ready
    toastQueue.push(toastData);
    console.warn(
      'Toast triggered before ToasterProvider mounted. Toast will be shown when ready.'
    );
    return null;
  }
};

/**
 * Toast API - Programmatic toast notifications
 */
export const toast = {
  /**
   * Show a success toast notification
   * @param message - The message to display
   * @param duration - Optional duration in ms (default: 5000)
   */
  success: (message: string, duration?: number): string | null => {
    return showToast(message, 'success', duration);
  },

  /**
   * Show an error toast notification
   * @param message - The message to display
   * @param duration - Optional duration in ms (default: 5000)
   */
  error: (message: string, duration?: number): string | null => {
    return showToast(message, 'error', duration);
  },

  /**
   * Show a warning toast notification
   * @param message - The message to display
   * @param duration - Optional duration in ms (default: 5000)
   */
  warning: (message: string, duration?: number): string | null => {
    return showToast(message, 'warning', duration);
  },

  /**
   * Show an info toast notification
   * @param message - The message to display
   * @param duration - Optional duration in ms (default: 5000)
   */
  info: (message: string, duration?: number): string | null => {
    return showToast(message, 'info', duration);
  },

  /**
   * Show a custom toast notification
   * @param message - The message to display
   * @param variant - The toast variant
   * @param duration - Optional duration in ms (default: 5000)
   */
  custom: (
    message: string,
    variant: ToastVariant,
    duration?: number
  ): string | null => {
    return showToast(message, variant, duration);
  },
};

/**
 * Hook-based toast API
 * Use this in React components for better type safety
 */
export const useToastApi = () => {
  return toast;
};
